import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { appointmentKeys } from '@/hooks/use-appointments';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { useMembership } from '@/hooks/use-membership';
import { serviceKeys } from '@/hooks/use-services';
import api from '@/services/api';
import { resetActingProvider } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import {
  BecomeTeamDto,
  CreateMemberDto,
  DisabledMemberAppointment,
  DisableMemberResult,
  HandoverItem,
  HandoverResult,
  InviteLink,
  Organization,
  PublicInvite,
  TeamMember,
  TeamReport,
  UpdateMemberDto,
  UpdateOrganizationDto,
} from '@/types/organization';

export const teamKeys = {
  all: ['team'] as const,
  organization: () => [...teamKeys.all, 'organization'] as const,
  members: () => [...teamKeys.all, 'members'] as const,
  futureAppointments: (memberId: string) =>
    [...teamKeys.all, 'future-appointments', memberId] as const,
  report: (from: string, to: string) => [...teamKeys.all, 'report', from, to] as const,
  publicInvite: (token: string) => [...teamKeys.all, 'public-invite', token] as const,
};

/**
 * O `membership` do `/user/me` diz o tipo da organização e quantos membros
 * ativos ela tem — a sidebar e o Perfil decidem por ele. Toda ação que muda
 * isso refaz o perfil, senão a sidebar ficaria sem "Equipe" depois de virar
 * organização.
 */
async function refreshMembership() {
  await Promise.resolve(useAuthStore.getState().fetchProfile());
}

function useInvalidateTeam() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: teamKeys.all });
    await refreshMembership();
  };
}

export function useOrganization(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();
  const { membership } = useMembership();

  return useQuery({
    queryKey: teamKeys.organization(),
    queryFn: async () => (await api.get<Organization>('/organization/me')).data,
    enabled: isHydrated && !!membership && options?.enabled !== false,
  });
}

/** Membros da equipe. Solo não tem o que listar (a API responderia 403). */
export function useTeamMembers(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();
  const { membership } = useMembership();

  return useQuery({
    queryKey: teamKeys.members(),
    queryFn: async () => (await api.get<TeamMember[]>('/organization/me/members')).data,
    enabled: isHydrated && membership?.type === 'TEAM' && options?.enabled !== false,
  });
}

export function useAddProfessional() {
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (data: CreateMemberDto) =>
      (await api.post<TeamMember>('/organization/me/members', data)).data,
    onSuccess: invalidate,
  });
}

export function useUpdateMember() {
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateMemberDto & { id: string }) =>
      (await api.patch<TeamMember>(`/organization/me/members/${id}`, data)).data,
    onSuccess: invalidate,
  });
}

/**
 * A resposta já traz os agendamentos futuros: vão direto para o cache da
 * lista de repasse, e o sheet abre sem esperar outra chamada.
 */
export function useDisableMember() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<DisableMemberResult>(`/organization/me/members/${id}/disable`)).data,
    onSuccess: async (result, id) => {
      await invalidate();
      queryClient.setQueryData(teamKeys.futureAppointments(id), result.futureAppointments);
    },
  });
}

/** Exclui quem já está desativado e sem horário pendente; o histórico fica. */
export function useRemoveMember() {
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/organization/me/members/${id}`);
    },
    onSuccess: invalidate,
  });
}

/** Agendamentos futuros que ainda estão na agenda de um membro desativado. */
export function useMemberFutureAppointments(
  memberId: string | undefined,
  options?: { enabled?: boolean },
) {
  const isHydrated = useAuthHydration();
  const { can } = useMembership();

  return useQuery({
    queryKey: teamKeys.futureAppointments(memberId ?? ''),
    queryFn: async () =>
      (
        await api.get<DisabledMemberAppointment[]>(
          `/organization/me/members/${memberId}/future-appointments`,
        )
      ).data,
    enabled: isHydrated && !!memberId && can('team') && options?.enabled !== false,
  });
}

/**
 * Repassa (mesmo horário, na agenda de outro membro) ou cancela com aviso ao
 * cliente. O que falha volta em `failed` e segue na lista.
 */
export function useHandover() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async ({ memberId, items }: { memberId: string; items: HandoverItem[] }) =>
      (
        await api.post<HandoverResult>(`/organization/me/members/${memberId}/handover`, {
          items,
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      await invalidate();
    },
  });
}

export function useEnableMember() {
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<TeamMember>(`/organization/me/members/${id}/enable`)).data,
    onSuccess: invalidate,
  });
}

/** Gera (ou renova) o link de convite: 7 dias, uso único. */
export function useInviteLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberId: string) =>
      (await api.post<InviteLink>(`/organization/me/members/${memberId}/invite`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamKeys.members() }),
  });
}

/**
 * Virar organização: nome + os três modos. A organização nasce com o plano de
 * equipe pendente: até o pagamento ser confirmado, o app só abre os planos.
 */
export function useBecomeTeam() {
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (data: BecomeTeamDto) =>
      (await api.post<Organization>('/organization/me/team', data)).data,
    onSuccess: invalidate,
  });
}

/** Voltar a ser solo: a API recusa enquanto houver outro membro ativo. */
export function useBackToSolo() {
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async () => (await api.delete<Organization>('/organization/me/team')).data,
    onSuccess: async () => {
      resetActingProvider();
      await invalidate();
    },
  });
}

/** Nome da organização e os três modos de operação. */
export function useOrganizationModes() {
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (data: UpdateOrganizationDto) =>
      (await api.patch<Organization>('/organization/me', data)).data,
    onSuccess: invalidate,
  });
}

/**
 * Relatório da organização inteira no período: totais e uma linha por
 * profissional. Não depende do foco.
 */
export function useTeamReport(from: string, to: string, options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();
  const { can } = useMembership();

  return useQuery({
    queryKey: teamKeys.report(from, to),
    queryFn: async () =>
      (await api.get<TeamReport>('/dashboard/team-report', { params: { from, to } })).data,
    enabled: isHydrated && can('teamReports') && options?.enabled !== false,
  });
}

// ---------------------------------------------------------------------------
// Convite (público, sem sessão)
// ---------------------------------------------------------------------------

export function usePublicInvite(token: string | undefined) {
  return useQuery({
    queryKey: teamKeys.publicInvite(token ?? ''),
    queryFn: async () => (await api.get<PublicInvite>(`/public/invite/${token}`)).data,
    enabled: !!token,
    retry: false,
  });
}

/** Manda o código para o WhatsApp que o dono informou. `devCode` só volta em dev. */
export function useSendInviteCode(token: string | undefined) {
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ sent: boolean; devCode?: string }>(`/public/invite/${token}/code`)).data,
  });
}
